import { useCallback, useEffect, useMemo, useState } from 'react';

import { getProductRelations, type ProductRelationEdge } from '../api/products';
import {
    explodeRows,
    type Exploded,
    type ExplodeOptions,
} from '../utils/skuExplosion';

/**
 * The explode toggle for a grid, and the rows it should render.
 *
 * ── Why it is a hook and not something DataSheet does ──────────────────────────────
 * DataSheet owns the CONTROL, because that is a piece of UI and belongs with the SKU column.
 * It cannot own the TRANSFORM, because it has no idea which of a row's fields are quantities
 * and which are ratios — and getting that wrong is exactly the bug the whole design exists to
 * avoid. So the grid calls this hook and hands the resulting `explosion` object to DataSheet.
 *
 * ── Why turning it off is free ────────────────────────────────────────────────────
 * Nothing is mutated. `explodeRows` clones before it writes, so `rows` and `explodedRows` are
 * two independent arrays and the toggle merely chooses which one to render. There is no undo
 * step that could drift out of sync with the data.
 *
 * ── Why filtering and grouping keep working ───────────────────────────────────────
 * The hook sits AFTER the fetch and after whatever filtering the container does, so any
 * change to `rows` re-runs the memo and the user sees the current selection exploded. Grids
 * that group must group the rows this returns, not their own — explode first, group second,
 * or the group totals are summed from pre-explosion leaves.
 */

/** The shape a grid spreads into `<DataSheet explosion={…} />`. */
export interface ExplosionControl {
    /** Whether the grid supports it at all — false while the graph is loading or empty. */
    available: boolean;
    enabled: boolean;
    onToggle: (enabled: boolean) => void;
    /** Menu label. */
    label: string;
    /** How many rows in the current view are parents that would be restated. */
    affectedRowCount: number;
    /**
     * Why the control cannot be used, or undefined when it can.
     *
     * Set when the relation graph could not be loaded. The control then renders disabled with
     * this as its tooltip rather than disappearing: a feature that silently removes itself when
     * its backing request fails is indistinguishable from a feature that was never built, and
     * that cost real debugging time.
     */
    error?: string;
}

export interface UseSkuExplosionOptions<T>
    extends Omit<ExplodeOptions<T>, 'edges'> {
    /** Off switch for grids that show materials, where the graph does not apply. */
    disabled?: boolean;
    /**
     * Lift the on/off flag into the caller. Needed by any grid whose FETCH depends on it:
     * a server-paginated grid has to ask for the whole set before it can explode, because a
     * parent on page 1 can break down into a base SKU on page 3, and one page's parents
     * folded into one page's children is not a number that means anything.
     *
     * That fetch runs earlier in the component than this hook can, so the flag has to be
     * declared above both. Omit the pair and the hook keeps its own state, which is right for
     * a grid that already holds every row.
     */
    enabled?: boolean;
    onEnabledChange?: (enabled: boolean) => void;
}

export interface UseSkuExplosionResult<T> {
    /** Exploded when the toggle is on, the caller's own array when it is off. */
    rows: Array<Exploded<T>>;
    explosion: ExplosionControl;
    /** True while `rows` is exploded — for column renderers that need to adapt. */
    isExploded: boolean;
    edges: ProductRelationEdge[];
}

/**
 * The relation graph, fetched once per session and shared by every grid that asks.
 *
 * A module-level promise rather than React Query state because the grids that want it are
 * scattered across routes and none of them owns it: the second caller should get the first
 * caller's request, not a duplicate. Cleared only on reload, which matches how rarely a
 * catalogue's combo structure changes.
 */
let relationsPromise: Promise<ProductRelationEdge[]> | null = null;

export function loadRelations(): Promise<ProductRelationEdge[]> {
    if (!relationsPromise) {
        relationsPromise = getProductRelations()
            .then((response) => response.edges ?? [])
            .catch((error) => {
                // Do not cache a failure — the next grid to mount should retry rather than
                // inherit a permanently empty graph and silently render the un-exploded view.
                relationsPromise = null;
                throw error;
            });
    }
    return relationsPromise;
}

export function useSkuExplosion<T>(
    options: UseSkuExplosionOptions<T>,
): UseSkuExplosionResult<T> {
    const {
        rows,
        getId,
        cloneRow,
        addScaled,
        recompute,
        makeEmptyRow,
        disabled,
        enabled: controlledEnabled,
        onEnabledChange,
    } = options;

    const [edges, setEdges] = useState<ProductRelationEdge[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [uncontrolledEnabled, setUncontrolledEnabled] = useState(false);
    const enabled = controlledEnabled ?? uncontrolledEnabled;

    useEffect(() => {
        if (disabled) return;
        let cancelled = false;
        loadRelations()
            .then((loaded) => {
                if (cancelled) return;
                setEdges(loaded);
                setLoadError(null);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                // Surfaced, not swallowed. The grid still works; only break-down is unavailable.
                // eslint-disable-next-line no-console
                console.warn('[SKU break-down] could not load product relations', error);
                setLoadError(
                    'Could not load product relations — break-down is unavailable. Check that the backend is running.',
                );
            });
        return () => {
            cancelled = true;
        };
    }, [disabled]);

    const parentIds = useMemo(
        () => new Set(edges.map((edge) => edge.parentId)),
        [edges],
    );

    const affectedRowCount = useMemo(
        () => (parentIds.size === 0 ? 0 : rows.filter((row) => parentIds.has(getId(row))).length),
        [rows, parentIds, getId],
    );

    const isExploded = enabled && !disabled && edges.length > 0;

    const explodedRows = useMemo(() => {
        if (!isExploded) return rows as Array<Exploded<T>>;
        return explodeRows({ rows, edges, getId, cloneRow, addScaled, recompute, makeEmptyRow });
    }, [isExploded, rows, edges, getId, cloneRow, addScaled, recompute, makeEmptyRow]);

    const onToggle = useCallback(
        (next: boolean) => {
            setUncontrolledEnabled(next);
            onEnabledChange?.(next);
        },
        [onEnabledChange],
    );

    return {
        rows: explodedRows,
        isExploded,
        edges,
        explosion: {
            /*
             * Offered whenever the merchant HAS a relation graph — deliberately not gated on
             * whether a parent happens to be among the rows currently on screen.
             *
             * That gate was wrong on any server-paginated grid: page 1 of the Inventory tab
             * may contain no combo at all, so the control vanished exactly where it was most
             * needed (turning it on is what fetches the full set), and it flickered in and out
             * as you paged. `affectedRowCount` still drives the tooltip copy.
             */
            // Shown when the graph loaded with edges, OR when loading it failed — in the
            // failure case as a disabled control carrying the reason.
            available: !disabled && (edges.length > 0 || loadError !== null),
            enabled: isExploded,
            onToggle,
            label: 'Break down to base SKUs',
            affectedRowCount,
            ...(loadError ? { error: loadError } : {}),
        },
    };
}
